import {
  AudioVideoFacade,
  DefaultDeviceController,
  DefaultMeetingSession,
  MeetingSessionConfiguration,
  NoOpLogger,
  PermissionDeniedError,
  VoiceFocusDeviceTransformer,
  VoiceFocusTransformDevice,
} from "amazon-chime-sdk-js"
import camelize from "camelize"

import airbrake from "src/airbrake-notifier"

import { ErrorType } from "../Cloudroom"

type ChimeMeetingJoinInfo = Record<string, unknown>

type ChimeAttendeeJoinInfo = {
  attendeeId: string
  joinToken: string
  externalUserId?: string | null
  capabilities?: unknown
}

export const createVoiceFocusTransformer = async (
  sessionId: string
): Promise<VoiceFocusDeviceTransformer | null> => {
  try {
    const isSupported = await VoiceFocusDeviceTransformer.isSupported()
    if (!isSupported) return null

    const transformer = await VoiceFocusDeviceTransformer.create()
    if (!transformer.isSupported()) return null

    return transformer
  } catch (e) {
    airbrake.notify({
      error: e,
      name: "Voice Focus initialization failed",
      params: { sessionId },
    })
    return null
  }
}

export const createChimeAudioVideo = (
  enableWebAudio: boolean,
  meeting: ChimeMeetingJoinInfo,
  attendee: ChimeAttendeeJoinInfo
) => {
  const logger = new NoOpLogger()
  const deviceController = new DefaultDeviceController(logger, {
    enableWebAudio,
  })
  const configuration = new MeetingSessionConfiguration(
    camelize(meeting),
    camelize(attendee)
  )
  const meetingSession = new DefaultMeetingSession(
    configuration,
    logger,
    deviceController
  )
  return meetingSession.audioVideo
}

export const startAudio = async (
  audioVideo: AudioVideoFacade,
  handleError: (error: ErrorType) => void,
  voiceFocusTransformer?: VoiceFocusDeviceTransformer | null
) => {
  try {
    const devices = await audioVideo.listAudioInputDevices()
    const audioInputDeviceInfo =
      devices.find(device => device.deviceId === "default") || devices[0]

    if (!audioInputDeviceInfo) {
      handleError({ name: "MissingDevice", message: "No microphone found" })
      return
    }

    if (!audioInputDeviceInfo.deviceId) {
      handleError({
        name: "DevicePermissions",
        message: "Microphone is blocked",
      })
      return
    }

    // Apply Voice Focus if transformer is present
    const audioInputDeviceId = audioInputDeviceInfo.deviceId
    let voiceFocusDevice: VoiceFocusTransformDevice | undefined

    if (voiceFocusTransformer) {
      try {
        voiceFocusDevice = await voiceFocusTransformer.createTransformDevice(
          audioInputDeviceId
        )
      } catch (e) {
        airbrake.notify({
          error: e,
          name: "Failed to apply Voice Focus",
        })
      }
    }

    await audioVideo.startAudioInput(voiceFocusDevice || audioInputDeviceId)
  } catch (e) {
    if (e instanceof PermissionDeniedError) {
      handleError({
        name: "DevicePermissions",
        message: "Microphone is blocked",
      })
    } else {
      handleError(e as ErrorType)
    }
  }
}

export const startVideo = (
  audioVideo: AudioVideoFacade,
  handleError: (error: ErrorType) => void
) => {
  audioVideo
    .listVideoInputDevices()
    .then(devices => {
      const videoInputDeviceInfo = devices[0]
      if (!videoInputDeviceInfo) {
        handleError({ name: "MissingDevice", message: "No camera found" })
      } else {
        if (!videoInputDeviceInfo.deviceId) {
          handleError({
            name: "DevicePermissions",
            message: "Camera is blocked",
          })
        } else {
          audioVideo
            .startVideoInput(videoInputDeviceInfo.deviceId)
            .then(() => audioVideo.startLocalVideoTile())
            .catch(e => {
              if (e instanceof PermissionDeniedError) {
                handleError({
                  name: "DevicePermissions",
                  message: "Camera is blocked",
                })
              } else handleError(e)
            })
        }
      }
    })
    .catch(handleError)
}

export const setUpVideoElement = ({
  audioVideo,
  local,
  attendeeId,
  tileId,
}: {
  audioVideo: AudioVideoFacade
  local: boolean
  attendeeId: string
  tileId: number
}) => {
  const videoElement = document.querySelector(
    local ? "#video-local" : `#video-${attendeeId}`
  ) as HTMLVideoElement

  if (videoElement) {
    audioVideo.bindVideoElement(tileId, videoElement)
    videoElement["disablePictureInPicture"] = false
  }
}
